<template>
  <section class="flex flex-col gap-2">
    <div
      v-if="showHeader"
      class="flex items-center gap-2 rounded-md bg-gray-100 dark:bg-slate-700"
      :class="{ 'pl-3': selectionState }"
    >
      <input
        v-if="selectionState"
        type="checkbox"
        data-test="group-select"
        class="h-4 w-4 shrink-0 cursor-pointer accent-primary"
        :checked="selectionState === 'all'"
        :indeterminate="selectionState === 'some'"
        :aria-label="$t('button.select')"
        @change="$emit('toggle-select-group')"
      />
      <button
        type="button"
        data-test="group-header"
        class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm font-semibold"
        :aria-expanded="!group.collapsed"
        @click="$emit('toggle-collapse')"
      >
        <FontAwesomeIcon
          :icon="['fal', group.collapsed ? 'chevron-right' : 'chevron-down']"
        />
        <span class="flex flex-1 items-baseline gap-2 truncate">
          <span class="truncate capitalize-first">
            {{ group.label ?? $t('global.not_specified') }}
          </span>
          <span
            v-if="subtitle"
            data-test="group-subtitle"
            class="truncate text-xs font-normal text-gray-500 dark:text-gray-400"
          >
            {{ subtitle }}
          </span>
        </span>
        <span class="rounded-lg bg-primary px-2 text-xs text-contrast-primary">
          {{ group.count }}
        </span>
      </button>
    </div>
    <template v-if="!group.collapsed">
      <slot />
      <div
        v-if="group.loadingMore"
        data-test="loading-more"
        aria-busy="true"
        :aria-label="$t('global.loading')"
      >
        <slot name="loading-more" />
      </div>
      <div v-if="group.has_more" class="flex justify-center">
        <BaseButton
          data-test="load-more"
          :title="$t('global.see_more_tasks')"
          size="small"
          :is-outline="true"
          :disabled="group.loadingMore"
          @click="$emit('load-more')"
        />
      </div>
    </template>
  </section>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { formatTaskDate } from '../utils/taskDates'

export default {
  name: 'TaskGroupSection',
  components: { BaseButton, FontAwesomeIcon },
  props: {
    group: { type: Object, required: true },
    showHeader: { type: Boolean, required: false, default: true },
    /** 'none' | 'some' | 'all' of the loaded tasks; null hides the select-all checkbox. */
    selectionState: { type: String, required: false, default: null }
  },
  emits: ['toggle-collapse', 'load-more', 'toggle-select-group'],
  computed: {
    /** Deceased groups only: their dossier reference and funeral date, or how many dossiers. */
    subtitle() {
      const header = this.group.header
      if (!header) return null
      if (header.dossier_count > 1) {
        return this.$t('global.dossiers_count', { count: header.dossier_count })
      }
      return (
        [
          header.dossier_reference,
          formatTaskDate(
            header.funeral_date,
            header.funeral_date_undefined_time
          )
        ]
          .filter(Boolean)
          .join(' · ') || null
      )
    }
  }
}
</script>
